import { Box, Typography } from '@mui/material';
import { useEffect, useRef, useState } from 'react';

interface Conversation {
  prompt: string;
  response: string;
}

const conversations: Conversation[] = [
  {
    prompt: 'How many websites use Shopify?',
    response:
      'NerdyData found 4,821,392 websites running Shopify. Want me to filter by country, or show you some examples?',
  },
  {
    prompt: 'Find Shopify stores that use Klaviyo but not Mailchimp',
    response:
      'Found 312,847 websites matching Shopify + Klaviyo − Mailchimp. I can export the full list as a CSV - just say the word.',
  },
  {
    prompt: 'What technologies does allbirds.com use?',
    response:
      'allbirds.com runs: Shopify, Klaviyo, Google Analytics 4, Meta Pixel, Attentive, and Yotpo Reviews.',
  },
];

type Phase = 'typing-prompt' | 'thinking' | 'typing-response' | 'pausing';

const BlinkingCursor = () => (
  <Box
    component="span"
    sx={{
      display: 'inline-block',
      width: '0.5em',
      height: '1em',
      backgroundColor: '#e2e8f0',
      ml: '2px',
      verticalAlign: 'text-bottom',
      animation: 'blink 1s step-end infinite',
      '@keyframes blink': {
        '0%, 100%': { opacity: 1 },
        '50%': { opacity: 0 },
      },
    }}
  />
);

const ThinkingDots = () => (
  <Box component="span">
    {[0, 1, 2].map((index) => {
      return (
        <Box
          key={index}
          component="span"
          sx={{
            display: 'inline-block',
            width: '6px',
            height: '6px',
            borderRadius: '50%',
            backgroundColor: '#4ade80',
            mx: '2px',
            verticalAlign: 'middle',
            animation: 'bounce 1.2s ease-in-out infinite',
            animationDelay: `${index * 0.2}s`,
            '@keyframes bounce': {
              '0%, 80%, 100%': { transform: 'translateY(0)' },
              '40%': { transform: 'translateY(-6px)' },
            },
          }}
        />
      );
    })}
  </Box>
);

export const FakeTerminal = () => {
  const [conversationIndex, setConversationIndex] = useState(0);
  const [phase, setPhase] = useState<Phase>('typing-prompt');
  const [displayedPrompt, setDisplayedPrompt] = useState('');
  const [displayedResponse, setDisplayedResponse] = useState('');
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const conversation = conversations[conversationIndex];
  useEffect(() => {
    if (!conversation) {
      return;
    }

    const clear = () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };

    if (phase === 'typing-prompt') {
      if (displayedPrompt.length < conversation.prompt.length) {
        timerRef.current = setTimeout(() => {
          setDisplayedPrompt(
            conversation.prompt.slice(0, displayedPrompt.length + 1)
          );
        }, 32);
      } else {
        timerRef.current = setTimeout(() => setPhase('thinking'), 400);
      }
    } else if (phase === 'thinking') {
      timerRef.current = setTimeout(() => setPhase('typing-response'), 720);
    } else if (phase === 'typing-response') {
      if (displayedResponse.length < conversation.response.length) {
        timerRef.current = setTimeout(() => {
          setDisplayedResponse(
            conversation.response.slice(0, displayedResponse.length + 1)
          );
        }, 18);
      } else {
        timerRef.current = setTimeout(() => setPhase('pausing'), 400);
      }
    } else if (phase === 'pausing') {
      timerRef.current = setTimeout(() => {
        setDisplayedPrompt('');
        setDisplayedResponse('');
        setConversationIndex((prev) => (prev + 1) % conversations.length);
        setPhase('typing-prompt');
      }, 2240);
    }

    return clear;
  }, [phase, displayedPrompt, displayedResponse, conversation]);

  return (
    <Box
      sx={{
        backgroundColor: '#1a1b2e',
        borderRadius: 2,
        overflow: 'hidden',
        fontFamily: 'monospace',
        boxShadow: '0 12px 40px rgba(0,0,0,0.45)',
        maxWidth: 740,
        width: '100%',
        mx: 'auto',
        userSelect: 'none',
      }}>
      {/* Title bar */}
      <Box
        sx={{
          backgroundColor: '#252640',
          px: 2,
          py: 1.25,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          borderBottom: '1px solid rgba(255,255,255,0.06)',
        }}>
        <Box
          sx={{
            width: 12,
            height: 12,
            borderRadius: '50%',
            backgroundColor: '#ff5f57',
          }}
        />
        <Box
          sx={{
            width: 12,
            height: 12,
            borderRadius: '50%',
            backgroundColor: '#ffbd2e',
          }}
        />
        <Box
          sx={{
            width: 12,
            height: 12,
            borderRadius: '50%',
            backgroundColor: '#28c940',
          }}
        />
        <Typography
          variant="caption"
          sx={{
            ml: 2,
            color: '#888',
            fontFamily: 'monospace',
            fontSize: '0.8rem',
          }}>
          Claude Code — NerdyData MCP
        </Typography>
      </Box>

      {/* Body */}
      <Box sx={{ p: 3, minHeight: 180 }}>
        {/* User prompt */}
        <Box sx={{ display: 'flex', gap: 1.5, mb: 2 }}>
          <Typography
            sx={{
              color: '#7c6af7',
              fontFamily: 'monospace',
              fontSize: '0.9rem',
              fontWeight: 'bold',
              flexShrink: 0,
            }}>
            You:
          </Typography>
          <Typography
            sx={{
              color: '#e2e8f0',
              fontFamily: 'monospace',
              fontSize: '0.9rem',
            }}>
            {displayedPrompt}
            {phase === 'typing-prompt' && <BlinkingCursor />}
          </Typography>
        </Box>

        {/* Response */}
        {phase !== 'typing-prompt' && (
          <Box sx={{ display: 'flex', gap: 1.5, mt: 1.5 }}>
            <Typography
              sx={{
                color: '#4ade80',
                fontFamily: 'monospace',
                fontSize: '0.9rem',
                fontWeight: 'bold',
                flexShrink: 0,
              }}>
              NerdyData:
            </Typography>
            <Typography
              sx={{
                color: '#e2e8f0',
                fontFamily: 'monospace',
                fontSize: '0.9rem',
              }}>
              {phase === 'thinking' ? (
                <ThinkingDots />
              ) : (
                <>
                  {displayedResponse}
                  {phase === 'typing-response' && <BlinkingCursor />}
                </>
              )}
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};
